import { useState, useEffect } from 'preact/hooks';
import { getUsers, getDepartments, getLocations, getAllUsers, updateUser, activateUser, deactivateUser, deleteUser, restoreUser, permanentlyDeleteUser, getToil, addToil, searchSlackUsers, importSlackUser, getSlackMemberCounts, bulkImportFromSlack, getAllUserBalances, getLeaveTypes, getLeaveInstances, createLeaveInstance, closeLeaveInstance, cancelLeaveInstance, getWorkspaceSettings } from '../lib/api';
import { useI18n } from '../i18n/I18nContext';

export function TeamPage({ user, workspace }) {
  const { t, locale } = useI18n();
  const [activeTab, setActiveTab] = useState('active');
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [userBalances, setUserBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ department: '', location: '', search: '', role: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showToilModal, setShowToilModal] = useState(false);
  const [toilUser, setToilUser] = useState(null);
  const [toilBalance, setToilBalance] = useState(null);
  const [toilForm, setToilForm] = useState({ hours: '', reason: '' });
  const [addingToil, setAddingToil] = useState(false);
  // Invite member state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [inviteSearching, setInviteSearching] = useState(false);
  const [importing, setImporting] = useState(null);
  // Bulk import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportCounts, setBulkImportCounts] = useState(null);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportFilter, setBulkImportFilter] = useState('all');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  // Leave instances state (for per-event tracking like maternity)
  const [showInstancesModal, setShowInstancesModal] = useState(false);
  const [instancesUser, setInstancesUser] = useState(null);
  const [userInstances, setUserInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [showCreateInstanceForm, setShowCreateInstanceForm] = useState(false);
  const [instanceForm, setInstanceForm] = useState({ leave_type_id: '', name: '', start_date: '', entitlement: '' });
  const [creatingInstance, setCreatingInstance] = useState(false);
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  // Format date according to workspace settings
  function formatDateDisplay(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00'); // Add time to avoid timezone issues
    if (isNaN(date.getTime())) return dateStr;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD/MM/YYYY':
      default:
        return `${day}/${month}/${year}`;
    }
  }

  // Get leave types that support per-instance tracking
  const perInstanceLeaveTypes = leaveTypes.filter(lt => lt.track_per_instance === 1);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  async function loadData() {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      const [usersData, deptsData, locsData, allData, leaveTypesData, balancesData, settingsData] = await Promise.all([
        getUsers(activeTab),
        getDepartments(),
        getLocations(),
        getAllUsers(),
        getLeaveTypes(),
        getAllUserBalances(currentYear),
        getWorkspaceSettings()
      ]);
      setUsers(usersData || []);
      setDepartments(deptsData || []);
      setLocations(locsData || []);
      setAllUsers(allData || []);
      setLeaveTypes(leaveTypesData || []);
      if (settingsData?.date_format) {
        setDateFormat(settingsData.date_format);
      }

      // Organize balances by user_id for easy lookup
      const balancesByUser = {};
      (balancesData || []).forEach(b => {
        if (!balancesByUser[b.user_id]) {
          balancesByUser[b.user_id] = {};
        }
        balancesByUser[b.user_id][b.leave_type_id] = b;
      });
      setUserBalances(balancesByUser);
    } catch (error) {
      console.error('Failed to load team data:', error);
    } finally {
      setLoading(false);
    }
  }

  // Count users by status
  const counts = {
    active: allUsers.filter(u => u.is_active && !u.is_deleted).length,
    inactive: allUsers.filter(u => !u.is_active && !u.is_deleted).length,
    deleted: allUsers.filter(u => u.is_deleted).length
  };

  const filteredUsers = users.filter(u => {
    if (filter.department && u.department_id !== filter.department) return false;
    if (filter.location && u.location_id !== filter.location) return false;
    if (filter.role && u.role !== filter.role) return false;
    if (filter.search) {
      const search = filter.search.toLowerCase();
      const matchesName = u.name?.toLowerCase().includes(search);
      const matchesEmail = u.email?.toLowerCase().includes(search);
      const matchesSlack = u.slack_id?.toLowerCase().includes(search);
      if (!matchesName && !matchesEmail && !matchesSlack) return false;
    }
    return true;
  });

  async function handleDeactivate(userId) {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await deactivateUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to deactivate user');
    }
  }

  async function handleActivate(userId) {
    try {
      await activateUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to activate user');
    }
  }

  async function handleDelete(userId) {
    if (!confirm('Are you sure you want to delete this user? They will be moved to the Deleted tab.')) return;
    try {
      await deleteUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to delete user');
    }
  }

  async function handleRestore(userId) {
    try {
      await restoreUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to restore user');
    }
  }

  async function handlePermanentDelete(userId, userName) {
    if (!confirm(`Are you sure you want to PERMANENTLY DELETE ${userName}?\n\nThis action cannot be undone. All their leave history and balances will be deleted.`)) return;
    // Double confirmation for permanent deletion
    if (!confirm('This is permanent! Click OK to confirm permanent deletion.')) return;
    try {
      await permanentlyDeleteUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to permanently delete user: ' + (err.message || 'Unknown error'));
    }
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function openEditModal(member) {
    // Parse working_days if it's a string, or use null if not set
    let workingDays = null;
    if (member.working_days) {
      try {
        workingDays = typeof member.working_days === 'string'
          ? JSON.parse(member.working_days)
          : member.working_days;
      } catch (e) {
        workingDays = null;
      }
    }

    setEditingUser({
      ...member,
      department_id: member.department_id || '',
      location_id: member.location_id || '',
      holiday_location_id: member.holiday_location_id || '',
      manager_id: member.manager_id || '',
      role: member.role || 'user',
      start_date: member.start_date || '',
      birthday: member.birthday || '',
      working_days: workingDays,
      use_custom_working_days: workingDays !== null
    });
    setShowEditModal(true);
  }

  async function handleSaveUser() {
    if (!editingUser) return;
    setSaving(true);
    try {
      await updateUser(editingUser.id, {
        department_id: editingUser.department_id || null,
        location_id: editingUser.location_id || null,
        holiday_location_id: editingUser.holiday_location_id || null,
        manager_id: editingUser.manager_id || null,
        role: editingUser.role,
        start_date: editingUser.start_date || null,
        birthday: editingUser.birthday || null,
        city: editingUser.city || null,
        working_days: editingUser.use_custom_working_days ? editingUser.working_days : null
      });
      setShowEditModal(false);
      setEditingUser(null);
      await loadData();
    } catch (err) {
      alert('Failed to save user');
    } finally {
      setSaving(false);
    }
  }

  async function openToilModal(member) {
    setToilUser(member);
    setToilForm({ hours: '', reason: '' });
    setToilBalance(null);
    setShowToilModal(true);

    // Load current TOIL balance
    try {
      const balance = await getToil(member.id);
      setToilBalance(balance);
    } catch (err) {
      console.error('Failed to load TOIL balance:', err);
    }
  }

  async function handleAddToil() {
    if (!toilUser || !toilForm.hours) return;
    setAddingToil(true);
    try {
      await addToil({
        user_id: toilUser.id,
        hours: parseFloat(toilForm.hours),
        reason: toilForm.reason
      });
      // Reload balance
      const balance = await getToil(toilUser.id);
      setToilBalance(balance);
      setToilForm({ hours: '', reason: '' });
      alert('TOIL hours added successfully');
    } catch (err) {
      alert('Failed to add TOIL: ' + (err.message || 'Unknown error'));
    } finally {
      setAddingToil(false);
    }
  }

  // Invite member functions
  async function openInviteModal() {
    setShowInviteModal(true);
    setInviteSearch('');
    setInviteSearchResults([]);
    // Load all available Slack users initially
    setInviteSearching(true);
    try {
      const result = await searchSlackUsers('');
      setInviteSearchResults(result.users || []);
    } catch (err) {
      console.error('Failed to load Slack users:', err);
    } finally {
      setInviteSearching(false);
    }
  }

  async function handleInviteSearch(query) {
    setInviteSearch(query);
    setInviteSearching(true);
    try {
      const result = await searchSlackUsers(query);
      setInviteSearchResults(result.users || []);
    } catch (err) {
      console.error('Failed to search Slack users:', err);
    } finally {
      setInviteSearching(false);
    }
  }

  async function handleImportUser(slackUser) {
    setImporting(slackUser.id);
    try {
      const result = await importSlackUser(slackUser.id);
      // Remove from search results
      setInviteSearchResults(prev => prev.filter(u => u.id !== slackUser.id));
      // Reload team data
      await loadData();
      alert(result.message || 'User imported successfully');
    } catch (err) {
      alert('Failed to import user: ' + (err.message || 'Unknown error'));
    } finally {
      setImporting(null);
    }
  }

  // Bulk import functions
  async function openBulkImportModal() {
    setShowBulkImportModal(true);
    setBulkImportCounts(null);
    setBulkImportResult(null);
    setBulkImportFilter('all');
    setBulkImportLoading(true);
    try {
      const counts = await getSlackMemberCounts();
      setBulkImportCounts(counts);
    } catch (err) {
      console.error('Failed to load Slack member counts:', err);
    } finally {
      setBulkImportLoading(false);
    }
  }

  function getImportCount() {
    if (!bulkImportCounts) return 0;
    const { slack } = bulkImportCounts;
    if (bulkImportFilter === 'all') return slack.total;
    if (bulkImportFilter === 'full_only') return slack.full;
    if (bulkImportFilter === 'include_multi') return slack.full + slack.multiChannel;
    return 0;
  }

  async function handleBulkImport() {
    setBulkImporting(true);
    setBulkImportResult(null);
    try {
      const result = await bulkImportFromSlack(bulkImportFilter);
      setBulkImportResult(result);
      await loadData();
    } catch (err) {
      setBulkImportResult({ error: err.message || 'Import failed' });
    } finally {
      setBulkImporting(false);
    }
  }

  // Instance management functions
  async function openInstancesModal(u) {
    setInstancesUser(u);
    setShowInstancesModal(true);
    setInstancesLoading(true);
    setShowCreateInstanceForm(false);
    setInstanceForm({ leave_type_id: '', name: '', start_date: '', entitlement: '' });
    try {
      const instances = await getLeaveInstances({ user_id: u.id });
      setUserInstances(instances || []);
    } catch (err) {
      console.error('Failed to load instances:', err);
      setUserInstances([]);
    } finally {
      setInstancesLoading(false);
    }
  }

  async function handleCreateInstance(e) {
    e.preventDefault();
    if (!instanceForm.leave_type_id || !instanceForm.start_date || !instanceForm.entitlement) return;
    setCreatingInstance(true);
    try {
      await createLeaveInstance({
        user_id: instancesUser.id,
        leave_type_id: instanceForm.leave_type_id,
        name: instanceForm.name || undefined,
        start_date: instanceForm.start_date,
        entitlement: parseFloat(instanceForm.entitlement)
      });
      // Reload instances
      const instances = await getLeaveInstances({ user_id: instancesUser.id });
      setUserInstances(instances || []);
      setShowCreateInstanceForm(false);
      setInstanceForm({ leave_type_id: '', name: '', start_date: '', entitlement: '' });
    } catch (err) {
      alert('Failed to create instance: ' + (err.message || 'Unknown error'));
    } finally {
      setCreatingInstance(false);
    }
  }

  async function handleCloseInstance(instanceId) {
    if (!confirm('Are you sure you want to close this instance? This will mark it as completed.')) return;
    try {
      await closeLeaveInstance(instanceId);
      const instances = await getLeaveInstances({ user_id: instancesUser.id });
      setUserInstances(instances || []);
    } catch (err) {
      alert('Failed to close instance: ' + (err.message || 'Unknown error'));
    }
  }

  async function handleCancelInstance(instanceId) {
    if (!confirm('Are you sure you want to cancel this instance?')) return;
    try {
      await cancelLeaveInstance(instanceId);
      const instances = await getLeaveInstances({ user_id: instancesUser.id });
      setUserInstances(instances || []);
    } catch (err) {
      alert('Failed to cancel instance: ' + (err.message || 'Unknown error'));
    }
  }

  const tabs = [
    { id: 'active', label: t('team.tab.active'), count: counts.active },
    { id: 'inactive', label: t('team.tab.inactive'), count: counts.inactive },
    { id: 'deleted', label: t('team.tab.deleted'), count: counts.deleted }
  ];

  if (loading && users.length === 0) {
    return (
      <div class="loading-screen">
        <div class="spinner" />
      </div>
    );
  }

  return (
    <div class="team-page">
      {/* Header */}
      <div class="bento-page-header-row">
        <div class="bento-page-header">
          <h1 class="bento-page-title">{t('team.page_title')}</h1>
          <p class="bento-page-subtitle">{t('team.page_subtitle')}</p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button class="btn btn-secondary" onClick={openBulkImportModal}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {t('team.action.bulk_import')}
            </button>
            <button class="btn btn-primary" onClick={openInviteModal}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              {t('team.action.invite_member')}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div class="bento-tabs" style={{ marginBottom: 'var(--bento-gap)' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            class={`bento-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <span class="tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div class="bento-card bento-filters-card" style={{ marginBottom: 'var(--bento-gap)' }}>
        <div class="filter-search">
          <svg class="search-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            class="form-input"
            placeholder={t('team.filter.search_placeholder')}
            value={filter.search}
            onInput={(e) => setFilter({ ...filter, search: e.target.value })}
          />
        </div>
        <div class="filter-selects">
          <select
            class="form-select"
            value={filter.department}
            onChange={(e) => setFilter({ ...filter, department: e.target.value })}
          >
            <option value="">{t('team.filter.all_departments')}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select
            class="form-select"
            value={filter.location}
            onChange={(e) => setFilter({ ...filter, location: e.target.value })}
          >
            <option value="">{t('team.filter.all_locations')}</option>
            {locations.map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          <select
            class="form-select"
            value={filter.role}
            onChange={(e) => setFilter({ ...filter, role: e.target.value })}
          >
            <option value="">{t('team.filter.all_roles')}</option>
            <option value="owner">{t('team.role.owner')}</option>
            <option value="admin">{t('team.role.admin')}</option>
            <option value="approver">{t('team.role.approver')}</option>
            <option value="user">{t('team.role.user')}</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div class="team-results-info">
        {t('team.showing_results', { showing: filteredUsers.length, total: users.length, status: t(`team.tab.${activeTab}`).toLowerCase() })}
      </div>

      {/* Team Grid */}
      <div class="bento-team-grid">
        {filteredUsers.length > 0 ? (
          filteredUsers.map(member => (
            <div key={member.id} class="bento-card bento-team-card">
              <div class="team-card-header">
                <div class="team-card-avatar">
                  {member.avatar_url ? (
                    <img
                      src={member.avatar_url}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span style={{ display: member.avatar_url ? 'none' : 'flex' }}>{member.name?.charAt(0) || '?'}</span>
                  {activeTab === 'active' && (
                    <span class="status-indicator online" />
                  )}
                </div>
                <div class="team-card-info">
                  <h3 class="team-card-name">{member.name}</h3>
                  {member.display_name && (
                    <span class="team-card-slack">@{member.display_name}</span>
                  )}
                </div>
                {isAdmin && (
                  <div class="team-card-menu">
                    <button class="btn-icon" onClick={() => openEditModal(member)}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div class="team-card-body">
                <div class="team-card-detail">
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{member.email || t('team.card.no_email')}</span>
                </div>
                <div class="team-card-detail">
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>{member.department_name || t('team.card.no_department')}</span>
                </div>
                <div class="team-card-detail">
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{member.location_name || t('team.card.no_location')}</span>
                </div>
                {member.manager_name && (
                  <div class="team-card-detail">
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{t('team.card.reports_to', { name: member.manager_name })}</span>
                  </div>
                )}
              </div>

              {/* Leave Balances */}
              {userBalances[member.id] && leaveTypes.length > 0 && (
                <div class="team-card-balances" style={{
                  padding: '0.75rem 1rem',
                  borderTop: '1px solid var(--border-color, #e5e7eb)',
                  background: 'var(--surface-bg, #f9fafb)'
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary, #6b7280)', marginBottom: '0.5rem' }}>
                    {t('team.card.leave_balances')}
                  </div>
                  <div class="flex flex-wrap gap-2">
                    {leaveTypes.filter(lt => lt.is_active !== false).map(lt => {
                      const balance = userBalances[member.id]?.[lt.id];
                      if (!balance) return null;

                      // Handle unlimited leave types (entitlement = -1)
                      const isUnlimited = balance.entitlement === -1 || lt.default_entitlement === -1;
                      const available = isUnlimited ? '∞' : (balance.entitlement || 0) + (balance.carried_over || 0) + (balance.adjustment || 0) - (balance.used || 0);
                      const total = isUnlimited ? '∞' : (balance.entitlement || 0) + (balance.carried_over || 0) + (balance.adjustment || 0);
                      const unitLabel = { hours: 'h', days: 'd', weeks: 'w', months: 'm' }[lt.unit] || 'd';

                      return (
                        <div
                          key={lt.id}
                          title={`${lt.name}: ${isUnlimited ? 'Unlimited' : available + unitLabel + ' available of ' + total + unitLabel}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid var(--border-color, #e5e7eb)',
                            fontSize: '0.75rem'
                          }}
                        >
                          <span>{lt.icon || '📋'}</span>
                          <span style={{ fontWeight: '600', color: isUnlimited || available > 0 ? 'var(--success, #10b981)' : 'var(--danger, #ef4444)' }}>
                            {isUnlimited ? '∞' : available}
                          </span>
                          <span style={{ color: 'var(--text-secondary, #9ca3af)' }}>
                            {isUnlimited ? '' : `/${total}${unitLabel}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div class="team-card-footer">
                <span class={`badge ${getRoleBadgeClass(member.role)}`}>
                  {member.role}
                </span>

                {isAdmin && activeTab === 'active' && (
                  <div class="team-card-actions">
                    <button class="btn btn-sm btn-secondary" onClick={() => openEditModal(member)}>
                      {t('common.edit')}
                    </button>
                    <button class="btn btn-sm btn-primary-outline" onClick={() => openToilModal(member)} title={t('team.action.toil_title')}>
                      {t('team.action.toil')}
                    </button>
                    {perInstanceLeaveTypes.length > 0 && (
                      <button class="btn btn-sm btn-primary-outline" onClick={() => openInstancesModal(member)} title={t('team.action.instances_title')}>
                        {t('team.action.instances')}
                      </button>
                    )}
                    <button class="btn btn-sm btn-danger-outline" onClick={() => handleDeactivate(member.id)}>
                      {t('team.action.deactivate')}
                    </button>
                    {member.role !== 'owner' && member.role !== 'admin' && (
                      <button class="btn btn-sm btn-danger" onClick={() => handlePermanentDelete(member.id, member.name)} title={t('team.action.remove_title')}>
                        {t('team.action.remove')}
                      </button>
                    )}
                  </div>
                )}

                {isAdmin && activeTab === 'inactive' && (
                  <div class="team-card-actions">
                    <button class="btn btn-sm btn-primary" onClick={() => handleActivate(member.id)}>
                      {t('team.action.activate')}
                    </button>
                    <button class="btn btn-sm btn-danger-outline" onClick={() => handleDelete(member.id)}>
                      {t('common.delete')}
                    </button>
                  </div>
                )}

                {isAdmin && activeTab === 'deleted' && (
                  <div class="team-card-actions">
                    <button class="btn btn-sm btn-primary" onClick={() => handleRestore(member.id)}>
                      {t('team.action.restore')}
                    </button>
                    {user?.role === 'owner' && member.role !== 'owner' && (
                      <button class="btn btn-sm btn-danger" onClick={() => handlePermanentDelete(member.id, member.name)}>
                        {t('team.action.remove')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div class="team-empty">
            <div class="empty-state-icon">
              {activeTab === 'active' ? '👥' : activeTab === 'inactive' ? '😴' : '🗑️'}
            </div>
            <h3>{t(`team.empty.${activeTab}_title`)}</h3>
            <p>
              {filter.search || filter.department || filter.location || filter.role
                ? t('team.empty.adjust_filters')
                : t(`team.empty.${activeTab}_description`)
              }
            </p>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <>
          <div class="modal-overlay" onClick={() => setShowEditModal(false)} />
          <div class="modal modal-scrollable" role="dialog" aria-modal="true">
            <div class="modal-header flex-shrink-0">
              <h2>{t('team.modal.edit_title')}</h2>
              <button class="btn-icon" onClick={() => setShowEditModal(false)} aria-label={t('common.close')}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body modal-body-scroll">
              <div class="edit-user-header">
                <div class="avatar avatar-lg">
                  {editingUser.avatar_url ? (
                    <img
                      src={editingUser.avatar_url}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span style={{ display: editingUser.avatar_url ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>{editingUser.name?.charAt(0) || '?'}</span>
                </div>
                <div>
                  <h3>{editingUser.name}</h3>
                  <p>{editingUser.email}</p>
                </div>
              </div>

              <div class="form-group">
                <label>{t('team.modal.role')}</label>
                {editingUser.role === 'owner' && user?.role !== 'owner' ? (
                  <div>
                    <input type="text" class="form-input" value={t('team.role.owner')} disabled style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                    <small class="text-secondary text-xs">{t('team.modal.owner_role_locked')}</small>
                  </div>
                ) : (
                  <select
                    class="form-select"
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    disabled={editingUser.role === 'owner'}
                  >
                    <option value="user">{t('team.role.user')}</option>
                    <option value="approver">{t('team.role.approver')}</option>
                    <option value="admin">{t('team.role.admin')}</option>
                    {/* Only owners can see/assign owner role */}
                    {user?.role === 'owner' && <option value="owner">{t('team.role.owner')}</option>}
                  </select>
                )}
              </div>

              <div class="form-group">
                <label>{t('team.modal.department')}</label>
                <select
                  class="form-select"
                  value={editingUser.department_id}
                  onChange={(e) => setEditingUser({ ...editingUser, department_id: e.target.value })}
                >
                  <option value="">{t('team.modal.no_department')}</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div class="form-group">
                <label>{t('team.modal.location')}</label>
                <select
                  class="form-select"
                  value={editingUser.location_id}
                  onChange={(e) => setEditingUser({ ...editingUser, location_id: e.target.value })}
                >
                  <option value="">{t('team.modal.no_location')}</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div class="form-group">
                <label>{t('team.modal.holiday_location')}</label>
                <select
                  class="form-select"
                  value={editingUser.holiday_location_id}
                  onChange={(e) => setEditingUser({ ...editingUser, holiday_location_id: e.target.value })}
                >
                  <option value="">{t('team.modal.use_assigned_location')}</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <small class="text-secondary text-xs">
                  {t('team.modal.holiday_location_help')}
                </small>
              </div>

              <div class="form-group">
                <label>{t('team.modal.manager')}</label>
                <select
                  class="form-select"
                  value={editingUser.manager_id}
                  onChange={(e) => setEditingUser({ ...editingUser, manager_id: e.target.value })}
                >
                  <option value="">{t('team.modal.no_manager')}</option>
                  {allUsers.filter(u => u.id !== editingUser.id && u.is_active).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div class="form-group">
                <label>{t('team.modal.start_date')}</label>
                <input
                  type="date"
                  class="form-input"
                  value={editingUser.start_date?.split('T')[0] || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, start_date: e.target.value })}
                />
                {editingUser.start_date && (
                  <small class="text-secondary text-xs">
                    {formatDateDisplay(editingUser.start_date?.split('T')[0])}
                  </small>
                )}
              </div>

              <div class="form-group">
                <label>{t('team.modal.birthday')}</label>
                <input
                  type="date"
                  class="form-input"
                  value={editingUser.birthday?.split('T')[0] || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, birthday: e.target.value })}
                />
                <small class="text-secondary text-xs">
                  {editingUser.birthday ? formatDateDisplay(editingUser.birthday?.split('T')[0]) + ' • ' : ''}{t('team.modal.birthday_help')}
                </small>
              </div>

              <div class="form-group">
                <label>{t('team.modal.city')}</label>
                <input
                  type="text"
                  class="form-input"
                  value={editingUser.city || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, city: e.target.value })}
                  placeholder={t('team.modal.city_placeholder')}
                />
                <small class="text-secondary text-xs">
                  {t('team.modal.city_help')}
                </small>
              </div>

              {/* Custom Working Days Override */}
              <div class="form-group" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '1rem' }}>
                <label class="toggle-label">
                  <input
                    type="checkbox"
                    checked={editingUser.use_custom_working_days}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Enable custom working days - default to department or Mon-Fri
                        setEditingUser({
                          ...editingUser,
                          use_custom_working_days: true,
                          working_days: editingUser.working_days || [1, 2, 3, 4, 5]
                        });
                      } else {
                        // Disable - will use department default
                        setEditingUser({
                          ...editingUser,
                          use_custom_working_days: false,
                          working_days: null
                        });
                      }
                    }}
                  />
                  <span class="font-medium">{t('team.modal.custom_working_days')}</span>
                </label>
                <small class="block mt-1 text-secondary">
                  {t('team.modal.custom_working_days_help')}
                </small>
              </div>

              {editingUser.use_custom_working_days && (
                <div class="form-group">
                  <label>{t('team.modal.working_days')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {dayNames.map((day, idx) => (
                      <label
                        key={day}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '48px',
                          height: '40px',
                          border: '2px solid',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '0.875rem',
                          borderColor: editingUser.working_days?.includes(idx) ? '#3b82f6' : '#e5e7eb',
                          background: editingUser.working_days?.includes(idx) ? '#eff6ff' : 'white',
                          color: editingUser.working_days?.includes(idx) ? '#3b82f6' : '#6b7280',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editingUser.working_days?.includes(idx) || false}
                          onChange={(e) => {
                            const newDays = e.target.checked
                              ? [...(editingUser.working_days || []), idx].sort((a, b) => a - b)
                              : (editingUser.working_days || []).filter(d => d !== idx);
                            setEditingUser({ ...editingUser, working_days: newDays });
                          }}
                          class="hidden"
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                  <small class="block mt-2 text-secondary">
                    {t('team.modal.working_days_help')}
                  </small>
                </div>
              )}
            </div>
            <div class="modal-footer flex-shrink-0 bg-white">
              <button class="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                {t('common.cancel')}
              </button>
              <button class="btn btn-primary" onClick={handleSaveUser} disabled={saving}>
                {saving ? t('common.saving') : t('common.save_changes')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* TOIL Modal */}
      {showToilModal && toilUser && (
        <>
          <div class="modal-overlay" onClick={() => setShowToilModal(false)} />
          <div class="modal modal-scrollable" role="dialog" aria-modal="true">
            <div class="modal-header flex-shrink-0">
              <h2>{t('team.toil.title')}</h2>
              <button class="btn-icon" onClick={() => setShowToilModal(false)} aria-label={t('common.close')}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body modal-body-scroll">
              <div class="edit-user-header">
                <div class="avatar avatar-lg">
                  {toilUser.avatar_url ? (
                    <img
                      src={toilUser.avatar_url}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span style={{ display: toilUser.avatar_url ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>{toilUser.name?.charAt(0) || '?'}</span>
                </div>
                <div>
                  <h3>{toilUser.name}</h3>
                  <p>{t('team.toil.subtitle')}</p>
                </div>
              </div>

              {/* Current TOIL Balance */}
              <div class="toil-balance-card" style="background: var(--surface-bg); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
                <h4 style="margin: 0 0 0.5rem 0; font-size: 0.875rem; color: var(--text-secondary);">{t('team.toil.current_balance')}</h4>
                {toilBalance ? (
                  <div style="display: flex; gap: 1.5rem;">
                    <div>
                      <span style="font-size: 1.5rem; font-weight: 600; color: var(--primary);">{toilBalance.toil_available || 0}h</span>
                      <span style="color: var(--text-secondary); font-size: 0.875rem; margin-left: 0.25rem;">{t('team.toil.available')}</span>
                    </div>
                    <div>
                      <span style="font-size: 1rem; color: var(--text-secondary);">{toilBalance.toil_hours || 0}h {t('team.toil.granted')}</span>
                    </div>
                    <div>
                      <span style="font-size: 1rem; color: var(--text-secondary);">{toilBalance.toil_used || 0}h {t('team.toil.used')}</span>
                    </div>
                  </div>
                ) : (
                  <p style="color: var(--text-secondary); margin: 0;">{t('common.loading')}</p>
                )}
              </div>

              <div class="form-group">
                <label>{t('team.toil.hours_to_add')}</label>
                <input
                  type="number"
                  class="form-input"
                  step="0.5"
                  min="0.5"
                  placeholder={t('team.toil.hours_placeholder')}
                  value={toilForm.hours}
                  onInput={(e) => setToilForm({ ...toilForm, hours: e.target.value })}
                />
                <small style="color: var(--text-secondary);">{t('team.toil.hours_help')}</small>
              </div>

              <div class="form-group">
                <label>{t('team.toil.reason')}</label>
                <textarea
                  class="form-input"
                  rows="2"
                  placeholder={t('team.toil.reason_placeholder')}
                  value={toilForm.reason}
                  onInput={(e) => setToilForm({ ...toilForm, reason: e.target.value })}
                />
              </div>

              <div class="alert alert-info" style="background: #e8f4fd; padding: 0.75rem 1rem; border-radius: 6px; font-size: 0.875rem;">
                <strong>{t('common.note')}:</strong> {t('team.toil.note')}
              </div>
            </div>
            <div class="modal-footer flex-shrink-0 bg-white">
              <button class="btn btn-secondary" onClick={() => setShowToilModal(false)}>
                {t('common.close')}
              </button>
              <button class="btn btn-primary" onClick={handleAddToil} disabled={addingToil || !toilForm.hours}>
                {addingToil ? t('team.toil.adding') : t('team.toil.add_hours')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowInviteModal(false)} />
          <div class="modal" style="max-width: 600px; max-height: 90vh; display: flex; flex-direction: column;">
            <div class="modal-header" style="flex-shrink: 0;">
              <h2>{t('team.invite.title')}</h2>
              <button class="btn-icon" onClick={() => setShowInviteModal(false)} aria-label={t('common.close')}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body" style="flex: 1; overflow-y: auto; min-height: 0;">
              <p style="margin-bottom: 1rem; color: var(--text-secondary);">
                {t('team.invite.description')}
              </p>

              <div class="filter-search" style="margin-bottom: 1rem;">
                <svg class="search-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  class="form-input"
                  placeholder={t('team.invite.search_placeholder')}
                  value={inviteSearch}
                  onInput={(e) => handleInviteSearch(e.target.value)}
                />
              </div>

              <div class="invite-results" style="max-height: 400px; overflow-y: auto;">
                {inviteSearching ? (
                  <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
                    <div class="spinner" style="margin: 0 auto 0.5rem;" />
                    {t('team.invite.searching')}
                  </div>
                ) : inviteSearchResults.length === 0 ? (
                  <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
                    <div style="font-size: 2rem; margin-bottom: 0.5rem;">👥</div>
                    {inviteSearch
                      ? t('team.invite.no_results')
                      : t('team.invite.all_added')
                    }
                  </div>
                ) : (
                  <div style="display: flex; flex-direction: column; gap: 0.5rem;">
                    {inviteSearchResults.map(slackUser => (
                      <div
                        key={slackUser.id}
                        class="invite-user-row"
                        style="display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem; background: var(--surface-bg); border-radius: 8px;"
                      >
                        <div class="avatar" style="width: 40px; height: 40px; flex-shrink: 0;">
                          {slackUser.avatar_url ? (
                            <img
                              src={slackUser.avatar_url}
                              alt=""
                              style="width: 100%; height: 100%; border-radius: 50%;"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <span style={{ width: '100%', height: '100%', borderRadius: '50%', background: 'var(--primary)', color: 'white', display: slackUser.avatar_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {slackUser.name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div style="flex: 1; min-width: 0;">
                          <div style="display: flex; align-items: center; gap: 0.5rem;">
                            <span style="font-weight: 500;">{slackUser.name}</span>
                            {slackUser.is_guest && (
                              <span
                                class="badge"
                                style="font-size: 0.7rem; padding: 0.15rem 0.4rem; background: #fef3c7; color: #92400e;"
                              >
                                {slackUser.guest_type === 'single_channel' ? t('team.invite.single_channel_guest') : t('team.invite.multi_channel_guest')}
                              </span>
                            )}
                          </div>
                          <div style="font-size: 0.8rem; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                            {slackUser.email || slackUser.display_name || slackUser.id}
                          </div>
                        </div>
                        <button
                          class="btn btn-sm btn-primary"
                          onClick={() => handleImportUser(slackUser)}
                          disabled={importing === slackUser.id}
                          style="flex-shrink: 0;"
                        >
                          {importing === slackUser.id ? t('team.invite.adding') : t('common.add')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div class="modal-footer" style="flex-shrink: 0; background: white;">
              <button class="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowBulkImportModal(false)} />
          <div class="modal" style="max-width: 550px; max-height: 90vh; display: flex; flex-direction: column;">
            <div class="modal-header" style="flex-shrink: 0;">
              <h2>{t('team.bulk.title')}</h2>
              <button class="btn-icon" onClick={() => setShowBulkImportModal(false)} aria-label={t('common.close')}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body" style="flex: 1; overflow-y: auto; min-height: 0;">
              <p style="margin-bottom: 1.5rem; color: var(--text-secondary);">
                {t('team.bulk.description')}
              </p>

              {bulkImportLoading ? (
                <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
                  <div class="spinner" style="margin: 0 auto 0.5rem;" />
                  {t('team.bulk.loading')}
                </div>
              ) : bulkImportCounts ? (
                <>
                  {/* Member counts */}
                  <div style="background: var(--surface-bg); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
                    <h4 style="margin: 0 0 0.75rem 0; font-size: 0.875rem; color: var(--text-secondary);">{t('team.bulk.workspace_members')}</h4>
                    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; text-align: center;">
                      <div>
                        <div style="font-size: 1.5rem; font-weight: 600; color: var(--primary);">{bulkImportCounts.slack.full}</div>
                        <div style="font-size: 0.75rem; color: var(--text-secondary);">{t('team.bulk.full_members')}</div>
                      </div>
                      <div>
                        <div style="font-size: 1.5rem; font-weight: 600; color: var(--warning, #f59e0b);">{bulkImportCounts.slack.multiChannel}</div>
                        <div style="font-size: 0.75rem; color: var(--text-secondary);">{t('team.bulk.multi_channel_guests')}</div>
                      </div>
                      <div>
                        <div style="font-size: 1.5rem; font-weight: 600; color: var(--text-secondary);">{bulkImportCounts.slack.singleChannel}</div>
                        <div style="font-size: 0.75rem; color: var(--text-secondary);">{t('team.bulk.single_channel_guests')}</div>
                      </div>
                    </div>
                    {bulkImportCounts.imported > 0 && (
                      <div style="margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid var(--border-color); font-size: 0.875rem; color: var(--text-secondary);">
                        {t('team.bulk.already_imported', { count: bulkImportCounts.imported })}
                      </div>
                    )}
                  </div>

                  {/* Filter options */}
                  <div class="form-group" style="margin-bottom: 1.5rem;">
                    <label style="margin-bottom: 0.5rem; display: block; font-weight: 500;">{t('team.bulk.who_to_import')}</label>
                    <div style="display: flex; flex-direction: column; gap: 0.5rem;">
                      <label style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--surface-bg); border-radius: 8px; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s;" class={bulkImportFilter === 'all' ? 'selected' : ''}>
                        <input
                          type="radio"
                          name="guestFilter"
                          value="all"
                          checked={bulkImportFilter === 'all'}
                          onChange={() => setBulkImportFilter('all')}
                        />
                        <div>
                          <div style="font-weight: 500;">{t('team.bulk.all_members', { count: bulkImportCounts.slack.total })}</div>
                          <div style="font-size: 0.8rem; color: var(--text-secondary);">{t('team.bulk.all_members_desc')}</div>
                        </div>
                      </label>
                      <label style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--surface-bg); border-radius: 8px; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s;">
                        <input
                          type="radio"
                          name="guestFilter"
                          value="include_multi"
                          checked={bulkImportFilter === 'include_multi'}
                          onChange={() => setBulkImportFilter('include_multi')}
                        />
                        <div>
                          <div style="font-weight: 500;">{t('team.bulk.full_and_multi', { count: bulkImportCounts.slack.full + bulkImportCounts.slack.multiChannel })}</div>
                          <div style="font-size: 0.8rem; color: var(--text-secondary);">{t('team.bulk.full_and_multi_desc')}</div>
                        </div>
                      </label>
                      <label style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--surface-bg); border-radius: 8px; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s;">
                        <input
                          type="radio"
                          name="guestFilter"
                          value="full_only"
                          checked={bulkImportFilter === 'full_only'}
                          onChange={() => setBulkImportFilter('full_only')}
                        />
                        <div>
                          <div style="font-weight: 500;">{t('team.bulk.full_only', { count: bulkImportCounts.slack.full })}</div>
                          <div style="font-size: 0.8rem; color: var(--text-secondary);">{t('team.bulk.full_only_desc')}</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Import result */}
                  {bulkImportResult && (
                    <div style={`padding: 1rem; border-radius: 8px; margin-bottom: 1rem; ${bulkImportResult.error ? 'background: #fef2f2; color: #991b1b;' : 'background: #f0fdf4; color: #166534;'}`}>
                      {bulkImportResult.error ? (
                        <strong>{t('common.error')}: {bulkImportResult.error}</strong>
                      ) : (
                        <>
                          <strong>{t('team.bulk.import_complete')}</strong>
                          <div style="margin-top: 0.5rem;">
                            {t('team.bulk.import_result', { created: bulkImportResult.created, updated: bulkImportResult.updated })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
                  {t('team.bulk.load_failed')}
                </div>
              )}
            </div>
            <div class="modal-footer" style="flex-shrink: 0; background: white;">
              <button class="btn btn-secondary" onClick={() => setShowBulkImportModal(false)}>
                {bulkImportResult?.created > 0 ? t('common.done') : t('common.cancel')}
              </button>
              {bulkImportCounts && !bulkImportResult?.created && (
                <button
                  class="btn btn-primary"
                  onClick={handleBulkImport}
                  disabled={bulkImporting || getImportCount() === 0}
                >
                  {bulkImporting ? t('team.bulk.importing') : t('team.bulk.import_users', { count: getImportCount() })}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Leave Instances Modal (for per-event tracking like maternity) */}
      {showInstancesModal && instancesUser && (
        <>
          <div class="modal-overlay" onClick={() => setShowInstancesModal(false)} />
          <div class="modal" style={{ maxHeight: '90vh', maxWidth: '600px', display: 'flex', flexDirection: 'column' }}>
            <div class="modal-header flex-shrink-0">
              <h2>{t('team.instances.title')}</h2>
              <button class="btn-icon" onClick={() => setShowInstancesModal(false)} aria-label={t('common.close')}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body modal-body-scroll">
              <div class="edit-user-header" style={{ marginBottom: '1rem' }}>
                <div class="avatar avatar-lg">
                  {instancesUser.avatar_url ? (
                    <img
                      src={instancesUser.avatar_url}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span style={{ display: instancesUser.avatar_url ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>{instancesUser.name?.charAt(0) || '?'}</span>
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>{instancesUser.name}</h3>
                  <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{t('team.instances.subtitle')}</p>
                </div>
              </div>

              {instancesLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  <div class="spinner" style={{ margin: '0 auto 0.5rem' }} />
                  {t('team.instances.loading')}
                </div>
              ) : (
                <>
                  {/* Active instances */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{t('team.instances.active')}</span>
                      <button
                        class="btn btn-sm btn-primary"
                        onClick={() => setShowCreateInstanceForm(!showCreateInstanceForm)}
                      >
                        {showCreateInstanceForm ? t('common.cancel') : t('team.instances.new_instance')}
                      </button>
                    </h4>

                    {/* Create instance form */}
                    {showCreateInstanceForm && (
                      <form onSubmit={handleCreateInstance} style={{ background: 'var(--surface-bg)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                        <div class="form-group" style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>{t('team.instances.leave_type')}</label>
                          <select
                            class="form-select"
                            value={instanceForm.leave_type_id}
                            onChange={(e) => {
                              const lt = perInstanceLeaveTypes.find(t => t.id === e.target.value);
                              setInstanceForm({
                                ...instanceForm,
                                leave_type_id: e.target.value,
                                entitlement: lt?.default_entitlement || ''
                              });
                            }}
                            required
                          >
                            <option value="">{t('team.instances.select_leave_type')}</option>
                            {perInstanceLeaveTypes.map(lt => (
                              <option key={lt.id} value={lt.id}>{lt.icon} {lt.name} ({lt.unit})</option>
                            ))}
                          </select>
                        </div>

                        <div class="form-group" style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>{t('team.instances.name')}</label>
                          <input
                            type="text"
                            class="form-input"
                            placeholder={t('team.instances.name_placeholder')}
                            value={instanceForm.name}
                            onInput={(e) => setInstanceForm({ ...instanceForm, name: e.target.value })}
                          />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                          <div class="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>{t('team.instances.start_date')}</label>
                            <input
                              type="date"
                              class="form-input"
                              value={instanceForm.start_date}
                              onChange={(e) => setInstanceForm({ ...instanceForm, start_date: e.target.value })}
                              required
                            />
                            {instanceForm.start_date && (
                              <small style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
                                {formatDateDisplay(instanceForm.start_date)}
                              </small>
                            )}
                          </div>
                          <div class="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>{t('team.instances.entitlement')}</label>
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              class="form-input"
                              placeholder={t('team.instances.entitlement_placeholder')}
                              value={instanceForm.entitlement}
                              onInput={(e) => setInstanceForm({ ...instanceForm, entitlement: e.target.value })}
                              required
                            />
                          </div>
                        </div>

                        <button type="submit" class="btn btn-primary" disabled={creatingInstance} style={{ width: '100%' }}>
                          {creatingInstance ? t('team.instances.creating') : t('team.instances.create')}
                        </button>
                      </form>
                    )}

                    {/* List of active instances */}
                    {userInstances.filter(i => i.status === 'active').length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)', background: 'var(--surface-bg)', borderRadius: '8px' }}>
                        {t('team.instances.no_active')}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {userInstances.filter(i => i.status === 'active').map(instance => {
                          const lt = leaveTypes.find(t => t.id === instance.leave_type_id);
                          const remaining = instance.entitlement - instance.used;
                          return (
                            <div key={instance.id} style={{ background: 'var(--surface-bg)', padding: '0.75rem 1rem', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontSize: '1.5rem' }}>{lt?.icon || '📋'}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 500 }}>
                                  {lt?.name || t('common.unknown')}{instance.name ? ` - ${instance.name}` : ''}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                  {t('team.instances.remaining', { remaining, total: instance.entitlement, unit: lt?.unit || t('common.days') })}
                                  {instance.start_date && ` • ${t('team.instances.started', { date: new Date(instance.start_date).toLocaleDateString(locale) })}`}
                                </div>
                              </div>
                              <button
                                class="btn btn-sm btn-secondary"
                                onClick={() => handleCloseInstance(instance.id)}
                                title={t('team.instances.mark_complete')}
                              >
                                {t('team.instances.complete')}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Completed/cancelled instances */}
                  {userInstances.filter(i => i.status !== 'active').length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{t('team.instances.past')}</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {userInstances.filter(i => i.status !== 'active').map(instance => {
                          const lt = leaveTypes.find(t => t.id === instance.leave_type_id);
                          return (
                            <div key={instance.id} style={{ background: 'var(--surface-bg)', padding: '0.75rem 1rem', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem', opacity: 0.7 }}>
                              <span style={{ fontSize: '1.5rem' }}>{lt?.icon || '📋'}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 500 }}>
                                  {lt?.name || t('common.unknown')}{instance.name ? ` - ${instance.name}` : ''}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                  {t('team.instances.used', { used: instance.used, total: instance.entitlement, unit: lt?.unit || t('common.days') })}
                                  {instance.closed_at && ` • ${t('team.instances.closed', { date: new Date(instance.closed_at).toLocaleDateString(locale) })}`}
                                </div>
                              </div>
                              <span class="badge" style={{ background: instance.status === 'completed' ? '#d1fae5' : '#fef3c7', color: instance.status === 'completed' ? '#065f46' : '#92400e' }}>
                                {t(`team.instances.status_${instance.status}`)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div class="modal-footer flex-shrink-0 bg-white">
              <button class="btn btn-secondary" onClick={() => setShowInstancesModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function getRoleBadgeClass(role) {
  switch (role) {
    case 'owner':
      return 'badge-owner';
    case 'admin':
      return 'badge-admin';
    case 'approver':
      return 'badge-approver';
    default:
      return 'badge-user';
  }
}
